import { PageLayout } from '../components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Search,
  PlusCircle,
  CreditCard,
  Upload,
  CheckCircle,
  MessageSquare,
  Receipt,
  Scale,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Clock,
  User,
  Heart,
} from 'lucide-react';

const forDonorsSteps = [
  {
    icon: Search,
    title: '1. Find a Campaign',
    description: 'Browse approved campaigns on the Discover page. Use search to find causes you care about.',
  },
  {
    icon: CreditCard,
    title: '2. Make Your Payment',
    description: "View the campaign's payment QR code or bank details. Transfer your donation directly to the campaign owner using your preferred method.",
  },
  {
    icon: Upload,
    title: '3. Upload Proof of Payment',
    description: 'After making your payment, upload a screenshot or receipt as proof. Enter the exact amount you donated and optionally leave a message.',
  },
  {
    icon: Clock,
    title: '4. Wait for Verification',
    description: "The campaign owner will review your proof of payment. Once verified, your donation will be reflected in the campaign's progress.",
  },
  {
    icon: MessageSquare,
    title: '5. Leave a Comment',
    description: 'Show your support by leaving an encouraging comment on the campaign page!',
  },
];

const forCampaignersSteps = [
  {
    icon: PlusCircle,
    title: '1. Create a Campaign',
    description: 'Click "Start Campaign" and fill in your campaign details: title, description, image, target amount, and deadline.',
  },
  {
    icon: CreditCard,
    title: '2. Add Payment Details',
    description: 'Upload your payment QR code and/or add your bank account details. This is how donors will send you funds.',
  },
  {
    icon: Clock,
    title: '3. Wait for Approval',
    description: 'Your campaign will be reviewed by our admin team. Once approved, it will appear on the Discover page.',
  },
  {
    icon: CheckCircle,
    title: '4. Verify Donations',
    description: "When donors upload proof of payment, you'll see them in your Manage → Donations tab. Review and verify each donation to update your campaign's progress.",
  },
  {
    icon: Receipt,
    title: '5. Upload Spending Receipts',
    description: 'Show transparency by uploading receipts when you spend funds. Include the amount spent and a description. This updates your "Amount Spent" progress bar.',
  },
  {
    icon: MessageSquare,
    title: '6. Post Updates',
    description: "Keep your supporters informed by posting regular updates about your campaign's progress.",
  },
];

const donationFlow = [
  { step: 'Donor views campaign', icon: Search },
  { step: 'Donor pays via QR/bank', icon: CreditCard },
  { step: 'Donor uploads proof', icon: Upload },
  { step: 'Owner verifies payment', icon: CheckCircle },
  { step: 'Progress bar updates', icon: TrendingUp },
];

export default function HowToUsePage() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Scale className="w-4 h-4" />
            Getting Started
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            How to Use Fundfair
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A complete guide for donors and campaign creators
          </p>
        </div>

        {/* Important Notice */}
        <Card className="mb-12 border-yellow-200 bg-yellow-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Important: How Donations Work
            </h3>
            <p className="text-yellow-700 mb-4">
              Fundfair does <strong>not</strong> process payments directly. Here is how it works:
            </p>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {donationFlow.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full border border-yellow-200">
                    <item.icon className="w-4 h-4 text-yellow-700" />
                    <span className="text-yellow-800">{item.step}</span>
                  </div>
                  {index < donationFlow.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-yellow-400" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Username Notice */}
        <Card className="mb-12 border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              About Your Username
            </h3>
            <p className="text-muted-foreground">
              You can set a username in your <strong>Profile</strong> settings. This username will be visible to everyone 
              when you comment on campaigns or make donations. <strong>You do not need to use your real name</strong> – 
              feel free to choose any nickname or alias you prefer.
            </p>
          </CardContent>
        </Card>

        {/* Progress Bars Explanation */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle>Understanding the Progress Bars</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600 mt-1" />
              <div>
                <p className="font-medium text-green-800">Amount Collected</p>
                <p className="text-sm text-green-700">
                  Shows the total verified donations received. This updates when the campaign owner verifies donation proofs.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl">
              <TrendingDown className="w-6 h-6 text-red-600 mt-1" />
              <div>
                <p className="font-medium text-red-800">Amount Spent</p>
                <p className="text-sm text-red-700">
                  Shows how much of the collected funds have been used. Campaign owners update this by uploading spending receipts.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* For Donors */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">For Donors</h2>
          </div>
          <div className="grid gap-4">
            {forDonorsSteps.map((step, index) => (
              <Card key={index}>
                <CardContent className="p-5">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <step.icon className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                      <p className="text-muted-foreground text-sm">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* For Campaign Creators */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
              <PlusCircle className="w-5 h-5 text-accent-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">For Campaign Creators</h2>
          </div>
          <div className="grid gap-4">
            {forCampaignersSteps.map((step, index) => (
              <Card key={index}>
                <CardContent className="p-5">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <step.icon className="w-5 h-5 text-accent" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                      <p className="text-muted-foreground text-sm">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-foreground">Frequently Asked Questions</h2>
          
          <Card>
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Why does my campaign not appear on Discover?</h3>
                <p className="text-muted-foreground">
                  New campaigns start as &quot;Pending Review&quot;. An admin must approve it before it appears on the Discover page. 
                  Check your campaign status in &quot;My Campaigns&quot;.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Why is my donation not showing?</h3>
                <p className="text-muted-foreground">
                  Donations only appear after the campaign owner verifies your proof of payment. 
                  You can check the status of your donation in the &quot;My Donations&quot; section on the campaign page.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Is my payment secure?</h3>
                <p className="text-muted-foreground">
                  Fundfair does not handle money directly. You transfer funds directly to the campaign owner&apos;s 
                  bank account or e-wallet. Always verify the campaign and owner before donating.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">What should my proof of payment include?</h3>
                <p className="text-muted-foreground">
                  A clear screenshot showing the transfer amount, date, and recipient details. 
                  Transaction receipts from your banking app work best.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground mb-2">Can I use a nickname instead of my real name?</h3>
                <p className="text-muted-foreground">
                  Yes! Your username can be anything you prefer. It will be shown on your comments and donations. 
                  Go to Profile to set or change your username.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}
